/**
 * @file contractsReview.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Reports → Contracts Review, the screen's own rules (client
 *              only — the report's rules are the server's
 *              utils/contractsReview.js, which sends the months, flags and
 *              columns): the filters and what Clear Filters resets them to,
 *              the query string, what a row's two actions may do, and the
 *              hand-off that opens a contract on its Contracts tab.
 *
 *   "Open contract" lands on the contract's own tab (Compounds, Blends,
 *   Straights, Spot, Minerals, Bags, Other — or Milk Powder › Pricing) with
 *   its search box filled in: the customer's account number (else name) and
 *   the contract number (else the item code), archived ones shown. The
 *   contract tabs keep their filters in the nav store, so the hand-off writes
 *   them there before navigating — a whole filters object (they do not merge
 *   a stored one over their defaults), page 1, every search field.
 */

/** What Clear Filters resets to — the month given is the report's default (last month). */
export function clearedFilters(defaultMonth) {
  return { month: defaultMonth || '', includeTill: false, includeDrafts: false, onlyReview: true, flags: [], search: '' };
}

/** The pill's words for each filter that differs from Clear Filters'. */
export const FILTER_LABELS = {
  month: 'Month',
  includeTill: 'Till invoices',
  includeDrafts: 'Draft invoices',
  onlyReview: 'Only rows to review',
  flags: 'Flags',
  search: 'Search'
};

/** The report's query string (no page — the screen adds it). */
export function reportQuery(f = {}, { columnFilters = null } = {}) {
  const p = new URLSearchParams();
  if (f.month) p.set('month', f.month);
  if (f.includeTill) p.set('includeTill', 'true');
  if (f.includeDrafts) p.set('includeDrafts', 'true');
  p.set('onlyReview', f.onlyReview === false ? 'false' : 'true');
  if (Array.isArray(f.flags) && f.flags.length) p.set('flags', f.flags.join(','));
  if (f.search && String(f.search).trim()) p.set('search', String(f.search).trim());
  if (columnFilters) p.set('columnFilters', columnFilters);
  return p.toString();
}

// ── Opening a contract on its Contracts tab ─────────────────────────────────

const COMMON = { period: '', search: '', rep: '', mill: '', limit: '50', showArchived: true, showContracted: true };

/** Where each kind of contract lives: menu, tab, the tab's persisted-state prefix and its filters' shape. */
const TARGETS = {
  compounds: { menu: 'contract-pricing', tab: 'compounds', prefix: 'compoundPricing', defaults: COMMON, label: 'Contracts › Compounds' },
  blends: { menu: 'contract-pricing', tab: 'blends', prefix: 'blendPricing', defaults: { ...COMMON, page: 1 }, label: 'Contracts › Blends' },
  straights: {
    menu: 'contract-pricing', tab: 'straights', prefix: 'straightsPricing',
    defaults: { search: '', period: '', supplier: '', delivery_terms: '', limit: '50', showArchived: true, showContracted: true },
    label: 'Contracts › Straights'
  },
  spot: {
    menu: 'contract-pricing', tab: 'spot', prefix: 'spotPricing',
    defaults: { search: '', rep: '', mill: '', fromDate: '', toDate: '', limit: '20' }, label: 'Contracts › Spot'
  },
  minerals: { menu: 'contract-pricing', tab: 'minerals', prefix: 'mineralsContractPricing', defaults: COMMON, label: 'Contracts › Minerals' },
  bags: { menu: 'contract-pricing', tab: 'bags', prefix: 'bagsContractPricing', defaults: COMMON, label: 'Contracts › Bags' },
  other: { menu: 'contract-pricing', tab: 'other', prefix: 'otherContractPricing', defaults: COMMON, label: 'Contracts › Other' },
  'milk powder': {
    menu: 'milk-powder-pricing', tab: 'contracts', prefix: 'milkPowder.contract',
    defaults: { search: '', rep: '', pricedOn: '', limit: '50', activeOnly: false }, label: 'Milk Powder › Pricing'
  }
};

/** A row's report category → the contract tab it would be on (a product with no contract yet). */
const CATEGORY_TABS = {
  compounds: 'compounds', blends: 'blends', straights: 'straights', minerals: 'minerals',
  bags: 'bags', other: 'other', milk_powder: 'milk powder'
};

/** Where a contract (or, with none, the row's category) is opened. null when nowhere. */
export function contractTarget(category) {
  const key = String(category || '').trim().toLowerCase();
  return TARGETS[key] || TARGETS[CATEGORY_TABS[key]] || null;
}

/** A contract number worth searching by — not the "#id" the report names an unnumbered one by. */
const numberOf = (ref) => {
  const s = String(ref || '').trim();
  return !s || /^(Contract|Straights month) #\d+$/.test(s) || /^Milk powder price/.test(s) ? '' : s;
};

/** The words the contract tab is searched for: the customer and the contract number (else the item code). */
export function contractSearchTerm(row, ref = null) {
  const who = row.account_no ? row.account_no : (row.customer ? `"${row.customer}"` : '');
  const what = numberOf(ref ?? row.contract) || row.item_code || '';
  return [who, what].filter(Boolean).join(' ');
}

/**
 * Write the hand-off into the nav store (`store` — useNavStore.getState())
 * and return where to go: { menu, tab }. The tab's filters are replaced whole
 * (its own shape) with the search, every period and archived contracts shown.
 */
export function contractHandoff(store, target, term) {
  if (!store || !target) return null;
  if (target.prefix) {
    const key = `${target.prefix}.filters`;
    const stored = store.getPage(key);
    const base = stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
    store.setPage(key, { ...target.defaults, ...base, search: term || '', ...('period' in target.defaults ? { period: '' } : {}),
      ...('showArchived' in target.defaults ? { showArchived: true } : {}), ...('activeOnly' in target.defaults ? { activeOnly: false } : {}),
      ...('page' in target.defaults ? { page: 1 } : {}) });
    const pag = store.getPage(`${target.prefix}.pagination`);
    if (pag && typeof pag === 'object') store.setPage(`${target.prefix}.pagination`, { ...pag, page: 1 });
    // Every search field — a narrowed field list could hide the contract.
    if (typeof store.clearPage === 'function') store.clearPage(`${target.prefix}.searchFields`);
  }
  return { menu: target.menu, tab: target.tab };
}

/**
 * The row's two actions — never hidden, greyed with the reason.
 *   contract: "Contract these prices…" — the month's priced sale lines into
 *             the contract window (needs 'Edit contract pricing').
 *   open:     "Open contract" — the contract the sales took, else the one in
 *             force today, on its tab; with none, the tab the product's
 *             contract would be on ("Open in Contracts").
 */
export function rowActions(row, { canContract = false, contractBlock = null } = {}) {
  const lines = Array.isArray(row?.invoice_line_ids) ? row.invoice_line_ids : [];
  let contractWhy = null;
  if (!canContract) contractWhy = contractBlock || "Needs 'Edit contract pricing'";
  else if (row?.contract_block) contractWhy = row.contract_block;
  else if (!lines.length) contractWhy = 'No priced sale line to make a contract from';

  const shown = row?.contract
    ? { ref: row.contract, category: row.contract_category }
    : row?.today_contract
      ? { ref: row.today_contract.ref, category: row.today_contract.category }
      : null;
  const target = contractTarget(shown ? shown.category : row?.category_key);
  const openWhy = target ? null : (row?.not_for_contract || 'No contract screen for this product');
  return {
    contract: {
      disabled: !!contractWhy,
      title: contractWhy || `Make or change a contract from the price invoiced (${lines.length} line${lines.length === 1 ? '' : 's'})`
    },
    open: {
      disabled: !!openWhy,
      label: shown ? 'Open contract' : 'Open in Contracts',
      title: openWhy || (shown ? `Open ${shown.ref} on ${target.label}` : `Find this customer's ${row?.category ? row.category.toLowerCase() : ''} contracts on ${target.label}`),
      target,
      term: target ? contractSearchTerm(row || {}, shown ? shown.ref : '') : ''
    }
  };
}
